import { useCallback, useEffect, useState } from "react";
import { secondMs } from "verdi-common/timeConstants";
import MapEntityBase from "../../map-entities/map-entity-base";
import { makeProgressRingParams } from "../../map-entities/shared";

interface UseProgressRingParamsProps {
    model: MapEntityBase["model"];
    shouldTickFn: () => boolean;
}
/**
 * Handles progress ring params for a device.
 * For scheduled irrigations, the ring needs to tick to show the progress of the irrigation since during the event
 * there will not be any mode changes to trigger the progress ring to update.
 */
export function useProgressRingParams({ shouldTickFn, model }: UseProgressRingParamsProps) {
    const [progressRingParams, setProgressRingParams] = useState(() => makeProgressRingParams({ model }));
    const [shouldTick, setShouldTick] = useState(shouldTickFn());

    /**
     * Update the progress ring params and should tick state when the model changes
     */
    const evaluateProgressRingParams = useCallback(() => {
        setProgressRingParams(makeProgressRingParams({ model }));
        if (shouldTickFn()) {
            setShouldTick(true);
        }
    }, [model, shouldTickFn]);

    // Immediately evaluate when hook is mounted
    useEffect(() => {
        evaluateProgressRingParams();
    }, [evaluateProgressRingParams]);

    // Needs to "tick" on an interval to update the progress ring params
    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (shouldTick) {
            setProgressRingParams(makeProgressRingParams({ model }));

            interval = setInterval(() => {
                setProgressRingParams(makeProgressRingParams({ model }));
                if (!shouldTickFn()) {
                    setShouldTick(false);
                    clearInterval(interval);
                }
            }, 15 * secondMs);
        }
        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [model, shouldTick, shouldTickFn]);

    return {
        // if not ticking, return new params every re-render to ensure the progress ring is updated
        progressRingParams: shouldTick ? progressRingParams : makeProgressRingParams({ model }),
        evaluateProgressRingParams,
    };
}
